import { Button, pantryColors, pantryTypography } from '@pantry/ui'
import { Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import { usePantryStore } from '../../../../providers/PantryProvider'
import type {
  Tag,
  TagColorOption,
  TagDetailResponse,
  TagFormValues,
} from '../../../../types/tag'
import { useDeleteTagSlugHistory, useTagDetail } from '../../hooks/useTag'

const p = pantryColors
const F = pantryTypography.fontFamily

interface TagDrawerProps {
  open: boolean
  tag: Tag | null
  colorOptions: TagColorOption[]
  isSaving: boolean
  onClose: () => void
  onSave: (values: TagFormValues) => void
  onDelete: (id: number) => void
  // Raised by the drawer when a single 301-redirect (slug-history row) is
  // removed. The parent layout owns the toast infrastructure (see TagPage),
  // so success/error surface back through these callbacks instead of being
  // shown inline in the drawer. Mirrors Category's CategoryDrawer contract.
  onSlugHistoryDeleted?: (slug: string) => void
  onSlugHistoryDeleteFailed?: (error: unknown) => void
}

const schema = Yup.object({
  name: Yup.string().trim().required('Please enter a Name'),
  slug: Yup.string(),
  description: Yup.string(),
})

// Hydrate from the detail response first (it carries persisted `slugLocked`
// alongside the live slug history); fall back to the lightweight list cache
// when the detail query is still loading or fails. New tags default to
// unlocked so the slug auto-derives from the name until the user pins it.
const getInitialValues = (
  tag: Tag | null,
  detail: TagDetailResponse | null,
): TagFormValues => ({
  id: tag?.id,
  name: detail?.name ?? tag?.name ?? '',
  slug: detail?.slug ?? tag?.slug ?? '',
  description: detail?.description ?? tag?.description ?? '',
  visible: detail?.visible ?? tag?.visible ?? true,
  colorId: detail?.colorId ?? tag?.colorId ?? 'brand',
  style: detail?.style ?? tag?.style ?? 'filled',
  slugLocked: detail?.slugLocked ?? tag?.slugLocked ?? false,
})

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')

export const TagDrawer: React.FC<TagDrawerProps> = ({
  open,
  tag,
  colorOptions,
  isSaving,
  onClose,
  onSave,
  onDelete,
  onSlugHistoryDeleted,
  onSlugHistoryDeleteFailed,
}) => {
  const store = usePantryStore()
  const storeId = store?.storeId ?? ''

  // Detail query — fires only in edit mode (when `tag.id` is set). Source of
  // truth for `slugLocked` (the persisted state) and `slugHistory` (the active
  // 301-redirect chip section). On create, `tag` is null and the query stays
  // disabled.
  const { data: detailData } = useTagDetail(storeId, open ? tag?.id : null)
  const detail = detailData ?? null
  const slugHistory = detail?.slugHistory ?? []
  const deleteSlugHistory = useDeleteTagSlugHistory(storeId)

  const handleRemoveRedirect = async (slugHistoryId: string, slug: string) => {
    if (!tag?.id) return
    try {
      await deleteSlugHistory.mutateAsync({ tagId: tag.id, slugHistoryId })
      onSlugHistoryDeleted?.(slug)
    } catch (error) {
      // The list/detail invalidation rolls the chip back in if the call
      // fails, so the drawer state is self-healing. Bubble the error up so
      // the parent layout can surface a toast/dialog via its existing
      // showToast plumbing. Mirrors Category's pattern.
      onSlugHistoryDeleteFailed?.(error)
    }
  }

  if (!open) return null

  return (
    <Formik
      initialValues={getInitialValues(tag, detail)}
      validationSchema={schema}
      enableReinitialize
      onSubmit={(values) => onSave(values)}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        submitForm,
      }) => {
        const selectedColor =
          colorOptions.find((o) => o.id === values.colorId) ?? colorOptions[0]

        const getBadgeStyle = (styleId: 'filled' | 'outline' | 'subtle') => {
          if (styleId === 'outline') {
            return {
              background: 'transparent',
              color: selectedColor.textColor,
              border: `2px solid ${selectedColor.borderColor}`,
            }
          }
          if (styleId === 'subtle') {
            return {
              background: selectedColor.backgroundColor,
              color: selectedColor.textColor,
              opacity: 0.85,
              border: 'none',
            }
          }
          return {
            background: selectedColor.backgroundColor,
            color: selectedColor.textColor,
            border: 'none',
          }
        }

        const badgeStyle = getBadgeStyle(values.style)
        const showNameError = touched.name && !!errors.name

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.24)',
              display: 'flex',
              justifyContent: 'flex-end',
              zIndex: 50,
            }}
            onClick={onClose}
          >
            <div
              style={{
                width: 420,
                maxWidth: '100vw',
                height: '100vh',
                boxSizing: 'border-box',
                overflowX: 'hidden',
                background: p.white,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: `1px solid ${p.gray[200]}`,
                boxShadow: '0 8px 28px rgba(0,0,0,.16)',
                fontFamily: F,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: `1px solid ${p.gray[200]}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: p.gray[800],
                    }}
                  >
                    {tag ? tag.name || 'Tag Name' : 'New Tag'}
                  </div>
                  <div
                    style={{ fontSize: 12, color: p.gray[400], marginTop: 4 }}
                  >
                    Assigned to {tag?.count ?? 0} products
                  </div>
                </div>
                <button type="button" onClick={onClose} style={closeBtnStyle}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4l10 10M14 4L4 14" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: 24,
                }}
              >
                <div style={previewWrapStyle}>
                  <span style={{ fontSize: 11.5, color: p.gray[500] }}>
                    Preview:
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 600,
                      ...badgeStyle,
                    }}
                  >
                    {values.name || 'Tag Name'}
                  </span>
                </div>

                <SectionTitle
                  iconPath="M2 4h4v4H2zM2 10l2 2 4-4M10 5h3M10 8.5h3M10 12h2"
                  title="Identity"
                />
                <FieldWrap>
                  <label htmlFor="tag-name" style={fieldLabelStyle}>
                    Tag Name{' '}
                    <span style={labelHintStyle}>
                      · shown on the storefront
                    </span>
                  </label>
                  <input
                    id="tag-name"
                    name="name"
                    value={values.name}
                    onChange={(e) => {
                      // Mirror Category: typing in the Name field auto-derives
                      // the slug ONLY while the lock is open. The lock state is
                      // checked at the moment of typing — flipping the lock
                      // later does not retroactively re-derive an old slug.
                      handleChange(e)
                      if (!values.slugLocked) {
                        setFieldValue('slug', slugify(e.currentTarget.value))
                      }
                    }}
                    placeholder="e.g. Staff Pick"
                    style={inputStyle}
                  />
                  {showNameError ? (
                    <div style={errorTextStyle}>{errors.name}</div>
                  ) : null}
                </FieldWrap>
                <FieldWrap>
                  <label htmlFor="tag-description" style={fieldLabelStyle}>
                    Description{' '}
                    <span style={labelHintStyle}>· internal only</span>
                  </label>
                  <textarea
                    id="tag-description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    rows={2}
                    placeholder="What does this tag mean? When should it be applied?"
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: 72,
                      lineHeight: 1.5,
                    }}
                  />
                </FieldWrap>

                <SectionTitle
                  iconPath="M8.5 2.5l-4 8M2.5 9l-1.5-2 1.5-2M10.5 9l1.5-2-1.5-2"
                  title="URL Slug"
                />
                <FieldWrap>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <span
                      style={{
                        fontSize: 12.5,
                        color: p.gray[400],
                        fontFamily: 'monospace',
                      }}
                    >
                      ?tag=
                    </span>
                    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                      <input
                        name="slug"
                        value={values.slug}
                        onChange={handleChange}
                        disabled={values.slugLocked}
                        placeholder="staff-pick"
                        style={{
                          ...inputStyle,
                          fontFamily: 'monospace',
                          paddingRight: 32,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: values.slugLocked ? p.gray[500] : p.gray[800],
                          background: values.slugLocked ? p.gray[50] : p.white,
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFieldValue('slugLocked', !values.slugLocked)
                        }
                        title="Lock / unlock auto-generation"
                        aria-label={
                          values.slugLocked
                            ? 'Unlock slug to re-enable auto-generation'
                            : 'Lock slug to set manually'
                        }
                        style={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: values.slugLocked
                            ? p.brand.primary
                            : p.gray[400],
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          {values.slugLocked ? (
                            <>
                              <rect
                                x="2"
                                y="6.5"
                                width="10"
                                height="7"
                                rx="1.5"
                              />
                              <path
                                d="M4.5 6.5V4.5a2.5 2.5 0 015 0v2"
                                strokeLinecap="round"
                              />
                            </>
                          ) : (
                            <>
                              <rect
                                x="2"
                                y="6.5"
                                width="10"
                                height="7"
                                rx="1.5"
                              />
                              <path
                                d="M4.5 6.5V4.5a2.5 2.5 0 015 0"
                                strokeLinecap="round"
                              />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div
                    style={{ fontSize: 11, color: p.gray[400], marginTop: 2 }}
                  >
                    {values.slugLocked
                      ? 'Slug is manually locked. Click lock to re-enable auto-generation.'
                      : 'Auto-generated from tag name. Click lock to set manually.'}
                  </div>
                  {slugHistory.length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: p.gray[400],
                          marginBottom: 5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        <svg
                          viewBox="0 0 11 11"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          width="11"
                          height="11"
                        >
                          <path d="M1.5 5.5h8M7 3l3 2.5L7 8" />
                        </svg>
                        Active 301 redirects
                      </div>
                      <div>
                        {slugHistory.map((entry) => (
                          <div
                            key={entry.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '5px 10px',
                              background: p.amber[100],
                              border: `1px solid ${p.amber[200]}`,
                              borderRadius: 4,
                              fontSize: 11.5,
                              color: p.amber[700],
                              fontFamily: 'monospace',
                              marginBottom: 4,
                            }}
                          >
                            <span>/{entry.slug}</span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveRedirect(entry.id, entry.slug)
                              }
                              disabled={deleteSlugHistory.isPending}
                              aria-label={`Remove redirect /${entry.slug}`}
                              title="Remove this 301 redirect"
                              style={{
                                color: p.amber[700],
                                opacity: deleteSlugHistory.isPending
                                  ? 0.4
                                  : 0.6,
                                fontSize: 14,
                                lineHeight: 1,
                                background: 'none',
                                border: 'none',
                                cursor: deleteSlugHistory.isPending
                                  ? 'not-allowed'
                                  : 'pointer',
                                padding: 0,
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </FieldWrap>

                <SectionTitle
                  iconPath="M6.5 1.5a5 5 0 100 10 5 5 0 000-10z"
                  title="Colour & Style"
                />
                <FieldWrap>
                  <div style={fieldLabelStyle}>Colour</div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6,1fr)',
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    {colorOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setFieldValue('colorId', option.id)}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          borderRadius: 6,
                          cursor: 'pointer',
                          border:
                            values.colorId === option.id
                              ? `3px solid ${p.gray[800]}`
                              : '3px solid transparent',
                          background: option.backgroundColor,
                          outline: `1.5px solid ${option.borderColor}`,
                        }}
                        title={option.label}
                      />
                    ))}
                  </div>
                </FieldWrap>
                <FieldWrap>
                  <div style={fieldLabelStyle}>Badge Style</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {(['filled', 'outline', 'subtle'] as const).map(
                      (styleId) => (
                        <button
                          key={styleId}
                          type="button"
                          onClick={() => setFieldValue('style', styleId)}
                          style={{
                            flex: 1,
                            padding: 10,
                            borderRadius: 6,
                            border:
                              values.style === styleId
                                ? `2px solid ${p.brand.primary}`
                                : `2px solid ${p.gray[200]}`,
                            background:
                              values.style === styleId
                                ? p.brand.light
                                : p.white,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 7,
                          }}
                        >
                          <span
                            style={{
                              ...getBadgeStyle(styleId),
                              padding: '4px 10px',
                              borderRadius: 20,
                              fontSize: 11.5,
                            }}
                          >
                            Tag
                          </span>
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              color:
                                values.style === styleId
                                  ? p.brand.primary
                                  : p.gray[600],
                            }}
                          >
                            {styleId.charAt(0).toUpperCase() + styleId.slice(1)}
                          </span>
                        </button>
                      ),
                    )}
                  </div>
                </FieldWrap>

                <SectionTitle
                  iconPath="M6.5 4a2.5 2.5 0 100 5 2.5 2.5 0 000-5zM1 6.5S3 3 6.5 3 12 6.5 12 6.5 10 10 6.5 10 1 6.5 1 6.5z"
                  title="Visibility"
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: p.gray[700],
                      }}
                    >
                      Show on storefront
                    </div>
                    <div style={{ fontSize: 11.5, color: p.gray[400] }}>
                      Tag appears on product badges and storefront filter bar
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFieldValue('visible', !values.visible)}
                    style={{
                      width: 40,
                      height: 22,
                      borderRadius: 11,
                      border: 'none',
                      background: values.visible
                        ? p.brand.primary
                        : p.gray[200],
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: p.white,
                        position: 'absolute',
                        top: 3,
                        left: values.visible ? 21 : 3,
                        transition: 'left .2s',
                      }}
                    />
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div style={footerStyle}>
                <button
                  type="button"
                  style={dangerGhostBtnStyle}
                  disabled={!tag || isSaving}
                  onClick={() => tag && onDelete(tag.id)}
                >
                  Delete
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    style={ghostBtnStyle}
                    onClick={onClose}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <Button
                    variant="primary"
                    onClick={submitForm}
                    disabled={isSaving}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }}
    </Formik>
  )
}

const SectionTitle: React.FC<{ iconPath: string; title: string }> = ({
  iconPath,
  title,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      fontSize: 11,
      fontWeight: 700,
      color: p.gray[400],
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      marginBottom: 12,
      marginTop: 22,
    }}
  >
    <svg
      viewBox="0 0 13 13"
      fill="none"
      stroke={p.brand.primary}
      strokeWidth="1.5"
      width="13"
      height="13"
    >
      <path d={iconPath} />
    </svg>
    {title}
  </div>
)

const FieldWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
      marginBottom: 14,
    }}
  >
    {children}
  </div>
)

const previewWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '14px 16px',
  background: p.gray[50],
  borderRadius: 10,
  border: `1px solid ${p.gray[200]}`,
  marginBottom: 24,
}
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: p.gray[600],
}
const labelHintStyle: React.CSSProperties = {
  color: p.gray[400],
  fontWeight: 400,
}
const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 11px',
  border: `1px solid ${p.gray[300]}`,
  borderRadius: 6,
  fontSize: 13.5,
  color: p.gray[800],
  outline: 'none',
  fontFamily: F,
}
const footerStyle: React.CSSProperties = {
  padding: '16px 24px',
  borderTop: `1px solid ${p.gray[200]}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
}
const closeBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: p.gray[400],
  cursor: 'pointer',
}
const ghostBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 14px',
  borderRadius: 6,
  border: `1px solid ${p.gray[300]}`,
  background: 'transparent',
  color: p.gray[600],
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
}
const dangerGhostBtnStyle: React.CSSProperties = {
  ...ghostBtnStyle,
  border: `1px solid ${p.red[200]}`,
  color: p.red[700],
}
const errorTextStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 11,
  color: p.red[700],
}
